import useObserver from '@react-hook/resize-observer'
import { RefObject, useCallback, useEffect, useState } from 'react'

const getSizeFromEntry = (entry: ResizeObserverEntry, type: SizeType) => {
  const { top, left, right, bottom } = entry.contentRect
  switch (type) {
    case 'content':
      return {
        width: Math.floor(entry.contentRect.width),
        height: Math.floor(entry.contentRect.height),
        top: Math.floor(top),
        left: Math.floor(left),
        right: Math.floor(right),
        bottom: Math.floor(bottom),
      }
    case 'border':
      return {
        width: Math.floor(entry.borderBoxSize[0]?.inlineSize ?? 0),
        height: Math.floor(entry.borderBoxSize[0]?.blockSize ?? 0),
        top: Math.floor(top),
        left: Math.floor(left),
        right: Math.floor(right),
        bottom: Math.floor(bottom),
      }
    default:
      return {
        width: Math.floor(entry.contentBoxSize[0]?.inlineSize ?? 0),
        height: Math.floor(entry.contentBoxSize[0]?.blockSize ?? 0),
        top: Math.floor(top),
        left: Math.floor(left),
        right: Math.floor(right),
        bottom: Math.floor(bottom),
      }
  }
}

export type SizeType = 'content' | 'border' | 'scroll'
export interface Size {
  width: number
  height: number
  top: number
  left: number
  right: number
  bottom: number
}

export const useResizeObserver = (
  ref: RefObject<HTMLElement | null>,
  sizeType: SizeType,
  onResize?: (size: Size) => void,
): Size => {
  const [size, setSize] = useState<{
    width: number
    height: number
    top: number
    left: number
    right: number
    bottom: number
  }>({
    width: 0,
    height: 0,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  })

  const handleResize = useCallback(
    (e: ResizeObserverEntry) => {
      if (ref.current) {
        const size = getSizeFromEntry(e, sizeType)
        setSize(size)
        onResize?.(size)
      }
    },
    [ref, sizeType, onResize],
  )
  useEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect()
      setSize({
        width: rect.width,
        height: rect.height,
        top: rect.top,
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom,
      })
    }
  }, [ref])
  // Where the magic happens
  useObserver(ref, handleResize)
  return size
}
